import { Fragment, forwardRef } from 'react';
import { SelectionCard, SelectionCardContent, SelectionCardTitle } from '@components/SelectionCard';
import { OpenBankingIncomeQuery } from '@graphql/index';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { IconProps } from '@spark-web/icon';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { INCOME_VERIFICATION, OPEN_BANKING_INCOME_DETAILS } from '@utils';
import LockIconSVG from '../../../../assets/lock.svg?react';

interface OpenBankingConnectedAccountsProps {
  onConnect: () => void;
  isLoadingConnect: boolean;
  uniqueConnectedBanks: OpenBankingIncomeQuery['openBankingConsents'];
  isFetchingConnectedBanks: boolean;
}

const LockIcon = forwardRef<SVGSVGElement, IconProps>((props, ref) => <LockIconSVG {...props} ref={ref} />);

function OpenBankingConnectedAccounts({
  onConnect,
  uniqueConnectedBanks = [],
  isFetchingConnectedBanks,
}: OpenBankingConnectedAccountsProps) {
  const {
    AUTOFILL_WITH_CDR_FAST_TRACK_TEXT,
    AUTOFILL_WITH_CDR_COMPLETED_BANNER_TEXT,
    AUTOFILL_WITH_CDR_COMPLETED_FOOTER_DESCRIPTION,
  } = INCOME_VERIFICATION;

  const { CONNECTED_ACCOUNTS } = OPEN_BANKING_INCOME_DETAILS;

  const connectedBanks = () => {
    if (!uniqueConnectedBanks?.length) {
      return null;
    }

    return (
      <>
        <Box display="flex" marginTop="medium">
          <SelectionCardTitle weight="regular" size="standard">
            {CONNECTED_ACCOUNTS}
          </SelectionCardTitle>
        </Box>
        <Divider />
        {uniqueConnectedBanks.map((bank, index) => (
          <Fragment key={`${bank.institutionId ?? bank.institutionName ?? 'bank'}-${index}`}>
            <Box display="flex" alignItems="center" gap="small">
              {bank?.institutionLogoUrl && (
                <svg width="30" height="30">
                  <image xlinkHref={bank.institutionLogoUrl} width="30" height="30" />
                </svg>
              )}
              <Text weight="regular">{bank?.institutionName || ''}</Text>
            </Box>
            <Divider />
          </Fragment>
        ))}
      </>
    );
  };

  return (
    <Stack borderRadius="medium" gap="large" border="primary" borderWidth="standard">
      <SelectionCard isLoading={false}>
        <Stack width="full" gap="large">
          {isFetchingConnectedBanks === false && (
            <Alert tone="positive" icon={LockIcon}>
              <Text size="small" tone="neutral">
                {AUTOFILL_WITH_CDR_COMPLETED_BANNER_TEXT()}
              </Text>
            </Alert>
          )}
          <SelectionCardContent>
            <SelectionCardTitle>{AUTOFILL_WITH_CDR_FAST_TRACK_TEXT}</SelectionCardTitle>
            <Box width="full" background="surfaceMuted" padding="large" borderRadius="small" border="standard">
              <SelectionCardContent gap="large">
                {isFetchingConnectedBanks ? <Spinner tone="primary" /> : <>{connectedBanks()}</>}
              </SelectionCardContent>
              <SelectionCardContent marginTop="large">
                {AUTOFILL_WITH_CDR_COMPLETED_FOOTER_DESCRIPTION(onConnect)}
              </SelectionCardContent>
            </Box>
          </SelectionCardContent>
        </Stack>
      </SelectionCard>
    </Stack>
  );
}

export default OpenBankingConnectedAccounts;
